﻿import type { FunctionalComponent } from "preact";
import { useState, useEffect } from "preact/hooks";
import { type ArticleModel, magazineArticlesService } from "./magazine.service";
import { relatedArticles } from "./Components/related-articles";
import { MagazineArticle } from "./Components/magazine-article";
import { toUrlSlug, TAB_EVENTS } from "./magazine-helpers";

export interface ArticleListContainerProps {
  id: string;
  tag: string;
  take: number;
  useMagazineTemplate: boolean;
  relatedTitle: string;
}

export class PageCounters {
  hasMoreArticles = false;
  nextPageNr = 0;
}

export const ArticleListContainer: FunctionalComponent<ArticleListContainerProps> = ({
  id,
  tag,
  take,
  useMagazineTemplate,
  relatedTitle,
}: ArticleListContainerProps) => {
  const [articles, setArticles] = useState(new Array<ArticleModel>());
  const [magazineUrl, setMagazineUrl] = useState(String);
  const [pageCounters, setPageCounters] = useState(new PageCounters());
  const [filter, setFilter] = useState(tag);

  const appendPagesEvent = async (e: Event, pageNr: number): Promise<void> => {
    e.preventDefault();
    const magazineModel = await magazineArticlesService({
      id,
      page: pageNr.toString(),
      tag: toUrlSlug(filter),
      take: take.toString(),
    });

    setPageCounters({
      nextPageNr: magazineModel.nextPageNr,
      hasMoreArticles: magazineModel.hasMoreArticles,
    });
    setArticles([...articles, ...magazineModel.pageList]);
  };

  const getArticles = async (): Promise<void> => {
    const magazineModel = await magazineArticlesService({
      id,
      page: pageCounters.nextPageNr.toString(),
      tag: toUrlSlug(filter),
      take: take.toString(),
    });

    setArticles(magazineModel.pageList);
    setMagazineUrl(magazineModel.urlToMagazine);
    setPageCounters({
      nextPageNr: magazineModel.nextPageNr,
      hasMoreArticles: magazineModel.hasMoreArticles,
    });
  };

  useEffect(() => {
    getArticles().catch((e) => console.error("Error fetching articles: ", e));
  }, [filter]);

  const handleFilterChanged = (e: CustomEvent<{ filter: string }>): void => {
    setPageCounters({ hasMoreArticles: true, nextPageNr: 0 });
    setFilter(e.detail.filter);
  };

  useEffect(() => {
    document.addEventListener(TAB_EVENTS.UPDATE_MAGAZINE_FILTER, handleFilterChanged);
    return (): void => {
      document.removeEventListener(TAB_EVENTS.UPDATE_MAGAZINE_FILTER, handleFilterChanged, false);
    };
  }, []);

  return useMagazineTemplate
    ? MagazineArticle({ articles, pageCounters, onClickHandler: appendPagesEvent })
    : relatedArticles({ articles, magazineUrl, showIngress: false, relatedTitle });
};
